import React from "react";
import Layout from "../Layout";
import {
  Grid,
  makeStyles,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@material-ui/core";
import { Edit, Delete } from "@material-ui/icons/";
import { withRouter } from "react-router";
import useApi from "Hooks/Admin/useApi";
import useAuth from "Hooks/Admin/useAuth";
import MuiAlert from "@material-ui/lab/Alert";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(2),
  },
  table: {
    overflowX: "auto",
    tableLayout: "fixed",
  },
  keywordCol: {
    width: "96px",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  dateCol: {
    width: "120px",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  datePrimary: {
    display: "block",
  },
  dateSecondary: {
    display: "block",
    color: "rgba(0, 0, 0, 0.54)",
  },
  nthOfTypeOddRow: {
    backgroundColor: "#eee",
  },
  alert: {
    marginTop: theme.spacing(2),
  },
  buttonCol: {
    textAlign: "center",
    width: "128px",
  },
  buttonRed: {
    color: "rgb(220, 0, 78)",
    "&:hover": {
      backgroundColor: "rgba(220, 0, 78, 0.04)",
    },
  },
}));

function CollectionThemes(props) {
  const { request } = useApi();
  const auth = useAuth();
  const classes = useStyles();

  const [collectionThemes, setCollectionThemes] = React.useState([]);
  const [isShowModal, setIsShowModal] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(null);
  const [success, setSuccess] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);

  React.useEffect(() => {
    document.title = "Buzz Finder Admin - 絞り込みテーマ管理";

    if (props.location.search.indexOf("create") > -1) {
      setSuccess(true);
      setSuccessMessage("登録が完了しました");
    }
    if (props.location.search.indexOf("edit") > -1) {
      setSuccess(true);
      setSuccessMessage("更新が完了しました");
    }
    if (props.location.search.indexOf("delete") > -1) {
      setSuccess(true);
      setSuccessMessage("削除が完了しました");
    }
  }, []);

  React.useEffect(() => {
    if (!auth.state.token) {
      return;
    }
    fetchCollectionThemes();
  }, [auth.state.token]);

  const fetchCollectionThemes = () => {
    request({
      method: "GET",
      url: `admin/keywords`,
      data: { word_type: "filter" },
    }).then((result) => {
      setCollectionThemes(result.data.data);
    });
  };

  const formatDateTime = (value) => {
    if (!value) return { date: "-", time: "" };

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: "-", time: "" };

    const pad = (num) => String(num).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return {
      date: `${year}/${month}/${day}`,
      time: `${hours}:${minutes}:${seconds}`,
    };
  };

  const onClickEditButton = (id) => {
    props.history.push(`/admin/collectionthemes/edit/${id}`);
  };

  const openDialog = (id) => {
    setDeleteId(id);
    setIsShowModal(true);
  };

  const deleteCollectionTheme = () => {
    request({
      method: "DELETE",
      url: `admin/keywords/${deleteId}`,
    })
      .then(() => {
        setIsShowModal(false);
        setSuccess(true);
        setSuccessMessage("削除が完了しました");
        setError(false);
        fetchCollectionThemes();
      })
      .catch(() => {
        setIsShowModal(false);
        setError(true);
        setErrorMessage("削除に失敗しました");
      });
  };

  function Row(props) {
    const { row, index } = props;
    const isNthOfTypeOdd = index % 2 === 0 ? false : true;
    const createdAt = formatDateTime(row.created_at);
    const updatedAt = formatDateTime(row.updated_at);

    return (
      <React.Fragment>
        <TableRow className={isNthOfTypeOdd ? classes.nthOfTypeOddRow : null} data-testid={`collectionThemeList-row-${row.id}`}>
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.desc}</TableCell>
          <TableCell className={classes.keywordCol}>{row.words ? row.words.length : 0}</TableCell>
          <TableCell className={classes.dateCol}>
            <span className={classes.datePrimary}>{createdAt.date}</span>
            <span className={classes.dateSecondary}>{createdAt.time}</span>
          </TableCell>
          <TableCell className={classes.dateCol}>
            <span className={classes.datePrimary}>{updatedAt.date}</span>
            <span className={classes.dateSecondary}>{updatedAt.time}</span>
          </TableCell>
          <TableCell className={classes.buttonCol}>
            <IconButton
              color="primary"
              aria-label="編集"
              data-testid={`collectionThemeList-editButton-${row.id}`}
              onClick={() => onClickEditButton(row.id)}
            >
              <Edit />
            </IconButton>
            <IconButton
              className={classes.buttonRed}
              aria-label="削除"
              data-testid={`collectionThemeList-deleteButton-${row.id}`}
              onClick={() => openDialog(row.id)}
            >
              <Delete />
            </IconButton>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <Layout
      content={
        <>
          <Grid>
            <Paper elevation={3} className={classes.paper}>
              <Typography variant="h6" color="primary">
                絞り込みテーマ一覧
              </Typography>
              <Table className={classes.table} data-testid="collectionThemeList-table">
                <TableHead>
                  <TableRow>
                    <TableCell>テーマ名</TableCell>
                    <TableCell>概要</TableCell>
                    <TableCell className={classes.keywordCol}>キーワード数</TableCell>
                    <TableCell className={classes.dateCol}>登録日</TableCell>
                    <TableCell className={classes.dateCol}>更新日</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collectionThemes.map(function (row, index) {
                    return <Row key={row.id} index={index + 1} row={row} />;
                  })}
                </TableBody>
              </Table>
            </Paper>
            {success && (
              <MuiAlert
                elevation={6}
                variant="filled"
                severity="success"
                className={classes.alert}
                data-testid="collectionThemeList-successMessage"
              >
                {successMessage}
              </MuiAlert>
            )}
            {error && (
              <MuiAlert
                elevation={6}
                variant="filled"
                severity="error"
                className={classes.alert}
                data-testid="collectionThemeList-errorMessage"
              >
                {errorMessage}
              </MuiAlert>
            )}
            <Grid container justifyContent="center" className={classes.wrapper}>
              <Button
                variant="contained"
                color="primary"
                data-testid="collectionThemeList-createButton"
                onClick={() =>
                  props.history.push(`/admin/collectionthemes/create`)
                }
              >
                新規絞り込みテーマ登録
              </Button>
            </Grid>
          </Grid>
          <Dialog open={isShowModal} onClose={() => setIsShowModal(false)} data-testid="collectionThemeList-deleteDialog">
            <DialogContent>
              <DialogContentText>
                本当に削除してよろしいですか？
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button color="primary" data-testid="collectionThemeList-deleteDialog-confirmButton" onClick={() => deleteCollectionTheme()}>
                はい
              </Button>
              <Button color="primary" data-testid="collectionThemeList-deleteDialog-cancelButton" onClick={() => setIsShowModal(false)}>
                いいえ
              </Button>
            </DialogActions>
          </Dialog>
        </>
      }
    />
  );
}

export default withRouter(CollectionThemes);
